import React, { useState } from 'react';
import { FiX, FiArrowRight, FiCheckSquare, FiFolder, FiLayout, FiUsers, FiSettings, FiShield } from 'react-icons/fi';

/**
 * Help articles data - all the help content
 */
export const helpArticles = {
  // Getting Started
  'Creating your first project': {
    category: 'Getting Started',
    icon: FiCheckSquare,
    content: `
## Creating Your First Project

Welcome to Tasklina! Here's how to create your first project and get started.

### Step 1: Navigate to Projects
Click on "Projects" in the sidebar or click the "+" button to create a new project.

### Step 2: Enter Project Details
- **Project Name**: Give your project a descriptive name
- **Description**: Add a brief description of what this project is about
- **Privacy**: Choose if this is a private or team project

### Step 3: Choose Your View
Select your preferred view:
- **Kanban Board**: Drag and drop tasks between columns
- **List View**: See all tasks in a list format
- **Calendar View**: Visualize tasks on a calendar

### Step 4: Invite Team Members (Optional)
If you're working with a team, invite them by email to collaborate on the project.

### Tips for Success
- Start with a clear project goal
- Break down large tasks into smaller subtasks
- Use labels and priorities to organize work
- Check your project dashboard regularly
    `
  },
  'Adding and managing tasks': {
    category: 'Getting Started',
    icon: FiCheckSquare,
    content: `
## Adding and Managing Tasks

Tasks are the building blocks of your projects. Here's how to work with them effectively.

### Creating a Task
1. Click the "+" button in any project or board column
2. Enter a task title (required)
3. Add optional details:
   - Description
   - Due date
   - Priority (Low, Medium, High, Urgent)
   - Assignee
   - Labels

### Managing Tasks
- **Move tasks**: Drag and drop to change status or order
- **Edit tasks**: Click on a task to open details
- **Complete tasks**: Check the checkbox or move to "Done"
- **Delete tasks**: Use the menu (⋮) and select "Delete"

### Task Features
- **Subtasks**: Break down complex tasks
- **Comments**: Discuss tasks with your team
- **Attachments**: Add files and documents
- **Activity**: Track all changes

### Pro Tips
- Use keyboard shortcuts for faster task creation
- Set due dates to stay on track
- Use labels to categorize similar tasks
    `
  },
  'Inviting team members': {
    category: 'Getting Started',
    icon: FiCheckSquare,
    content: `
## Inviting Team Members

Collaborate effectively by inviting your team to Tasklina.

### How to Invite Members

#### Method 1: Project Level
1. Open your project
2. Click "Settings" or the gear icon
3. Go to "Members" tab
4. Enter email addresses
5. Choose their role
6. Click "Send Invitation"

#### Method 2: Organization Level
1. Go to Organization Settings
2. Navigate to "Users" or "Team"
3. Click "Invite Members"
4. Enter emails and select roles

### Member Roles
- **Viewer**: Can view tasks and comments
- **Editor**: Can create and edit tasks
- **Admin**: Full project management access

### Tips
- Invite members by email - they'll receive a link to join
- Set appropriate permissions for each member
- Use teams to organize large groups
    `
  },
  'Setting up notifications': {
    category: 'Getting Started',
    icon: FiCheckSquare,
    content: `
## Setting Up Notifications

Stay informed without being overwhelmed by customizing your notifications.

### Notification Types
- **Task assigned to you**
- **Task due soon**
- **Comments and mentions**
- **Project updates**
- **Team activity**

### Customizing Notifications
1. Go to **Settings** > **Notifications**
2. Toggle notifications on/off by type
3. Set email preferences:
   - Instant
   - Daily digest
   - Weekly summary
   - Off

### Quiet Hours
Set times when you don't want to receive notifications:
1. Enable "Quiet Hours"
2. Set start and end times
3. Choose which days

### Best Practices
- Enable notifications for tasks assigned to you
- Use mentions (@username) to alert specific people
- Review notification settings periodically
    `
  },

  // Projects & Tasks
  'Project organization': {
    category: 'Projects & Tasks',
    icon: FiFolder,
    content: `
## Project Organization

Keep your projects organized for maximum productivity.

### Project Structure
- **Boards/Lists**: Organize tasks by status or category
- **Labels**: Color-code tasks for quick identification
- **Milestones**: Set major checkpoints
- **Sections**: Group related tasks together

### Organization Tips
1. Use consistent naming conventions
2. Create templates for recurring project types
3. Archive completed projects
4. Use descriptions to document project goals

### Folder Structure
- Create folders to group related projects
- Use starred projects for quick access
- Filter projects by status or team

### Best Practices
- Review and clean up projects regularly
- Keep project descriptions up to date
- Use project templates for consistency
    `
  },
  'Task priorities and labels': {
    category: 'Projects & Tasks',
    icon: FiFolder,
    content: `
## Task Priorities and Labels

Use priorities and labels to organize and filter your tasks effectively.

### Priority Levels
- **🔴 Urgent**: Needs immediate attention
- **🟠 High**: Important, do soon
- **🟡 Medium**: Normal priority
- **🟢 Low**: Do when time allows

### Using Labels
Labels help categorize tasks by:
- Type (Bug, Feature, Improvement)
- Team (Design, Development, Marketing)
- Status (Blocked, In Review, Approved)

### Creating Labels
1. Open project settings
2. Go to "Labels" tab
3. Click "Create Label"
4. Choose name and color

### Filtering by Priority/Labels
- Use the filter bar to show specific items
- Combine filters (e.g., High priority + Bug label)
- Save frequently used filters

### Tips
- Don't overuse "Urgent" - reserve for true emergencies
- Create a consistent label system across projects
- Review and update priorities regularly
    `
  },
  'Due dates and reminders': {
    category: 'Projects & Tasks',
    icon: FiFolder,
    content: `
## Due Dates and Reminders

Never miss a deadline with effective use of due dates and reminders.

### Setting Due Dates
1. Open a task
2. Click the date field
3. Select a date from the calendar
4. Optionally set a specific time

### Reminder Options
- **At due time**: Get notified when task is due
- **1 hour before**: Pre-deadline reminder
- **1 day before**: Day-ahead reminder
- **1 week before**: Early warning for big tasks

### Calendar Integration
- Sync with Google Calendar
- View tasks in calendar view
- See deadlines across all projects

### Tips
- Set realistic due dates
- Use relative dates ("Next Friday")
- Review overdue tasks daily
- Reschedule rather than ignore missed dates
    `
  },
  'Subtasks and checklists': {
    category: 'Projects & Tasks',
    icon: FiFolder,
    content: `
## Subtasks and Checklists

Break down complex tasks into manageable pieces.

### Subtasks
Subtasks are mini-tasks within a parent task:
1. Open a task
2. Click "Add Subtask"
3. Enter subtask details
4. Track progress separately

### Checklists
Quick items to check off:
1. In task description, use [ ] for checkboxes
2. Or use the checklist feature
3. Check items as you complete them

### When to Use What
- **Subtasks**: For items that need tracking, assignees, or dates
- **Checklists**: For simple to-do items within a task

### Progress Tracking
- See completion percentage on parent task
- Filter by subtask status
- Roll up subtask progress to project level

### Tips
- Keep subtasks focused and small
- Don't nest too deeply
- Use checklists for repetitive processes
    `
  },

  // Views & Boards
  'Kanban board basics': {
    category: 'Views & Boards',
    icon: FiLayout,
    content: `
## Kanban Board Basics

Visual task management with drag-and-drop simplicity.

### What is Kanban?
A visual system where tasks move through columns representing different stages.

### Default Columns
- **To Do**: Tasks waiting to be started
- **In Progress**: Tasks being worked on
- **Done**: Completed tasks

### Customizing Columns
1. Click "+" to add new columns
2. Drag columns to reorder
3. Rename columns by clicking the title
4. Set column limits (WIP limits)

### Moving Tasks
- Drag and drop between columns
- Click task to change status
- Use keyboard shortcuts

### Best Practices
- Limit work in progress
- Move tasks left-to-right
- Keep columns clean and organized
- Archive completed tasks regularly
    `
  },
  'List and table views': {
    category: 'Views & Boards',
    icon: FiLayout,
    content: `
## List and Table Views

Alternative ways to view and manage your tasks.

### List View
A vertical list of all tasks:
- See all tasks at a glance
- Group by status, priority, or assignee
- Expand/collapse sections
- Quick inline editing

### Table View
Spreadsheet-style task management:
- Custom columns
- Sort and filter
- Bulk editing
- Export to CSV

### Switching Views
1. Click the view icon in the toolbar
2. Select your preferred view
3. Views are saved per project

### When to Use Each
- **Kanban**: Visual workflow management
- **List**: Simple task lists
- **Table**: Data-heavy projects, reporting

### Tips
- Try different views to find what works best
- Use table view for bulk updates
- Combine with filters for powerful queries
    `
  },
  'Filtering and sorting': {
    category: 'Views & Boards',
    icon: FiLayout,
    content: `
## Filtering and Sorting

Find exactly what you need with powerful filters.

### Basic Filtering
- Click the filter icon in the toolbar
- Select criteria:
  - Assignee
  - Priority
  - Due date
  - Labels
  - Status

### Advanced Filters
Combine multiple criteria:
- AND: All conditions must match
- OR: Any condition can match
- NOT: Exclude certain items

### Sorting Options
- Due date (earliest/latest)
- Priority (highest/lowest)
- Created date
- Alphabetical
- Custom order

### Saving Filters
1. Set up your filter
2. Click "Save Filter"
3. Name your filter
4. Access from saved filters menu

### Tips
- Use "My Tasks" filter for personal work
- Save commonly used filters
- Clear filters to see all tasks
    `
  },
  'Saved views': {
    category: 'Views & Boards',
    icon: FiLayout,
    content: `
## Saved Views

Create custom views for different needs.

### What are Saved Views?
Combinations of:
- View type (Kanban, List, Table)
- Filters applied
- Sort order
- Column visibility

### Creating a Saved View
1. Set up your preferred view
2. Apply filters and sorting
3. Click "Save View"
4. Give it a descriptive name

### Use Cases
- "My Urgent Tasks" - High priority assigned to me
- "This Week" - Tasks due in next 7 days
- "Team Overview" - All tasks grouped by assignee
- "Bug Tracker" - Tasks with "Bug" label

### Managing Views
- Edit view settings
- Share views with team
- Set default view for project
- Delete unused views

### Tips
- Create views for different workflows
- Share useful views with your team
- Review and update views periodically
    `
  },

  // Team Collaboration
  'Team workspaces': {
    category: 'Team Collaboration',
    icon: FiUsers,
    content: `
## Team Workspaces

Organize your team's work in dedicated spaces.

### What is a Workspace?
A shared environment where teams can:
- Create and manage projects
- Collaborate on tasks
- Share files and resources
- Track progress together

### Creating a Workspace
1. Go to workspace settings
2. Click "Create Workspace"
3. Name your workspace
4. Invite team members

### Workspace Features
- Shared projects
- Team member management
- Permissions and roles
- Activity dashboard
- Team calendar

### Best Practices
- Create separate workspaces for different teams
- Use clear naming conventions
- Set up permissions carefully
- Keep workspace settings documented
    `
  },
  'Comments and mentions': {
    category: 'Team Collaboration',
    icon: FiUsers,
    content: `
## Comments and Mentions

Communicate effectively within tasks.

### Adding Comments
1. Open any task
2. Scroll to comments section
3. Type your message
4. Press Enter or click Send

### Using Mentions
- Type @ followed by a username
- The mentioned person gets notified
- They can click to jump to the task

### Rich Comments
- **Bold** text with **text**
- _Italic_ with _text_
- \`Code\` with backticks
- Links are auto-detected
- Attach images and files

### Best Practices
- Keep comments focused and relevant
- Use mentions sparingly
- Reply in threads to keep discussions organized
- Edit comments if needed
    `
  },
  'Activity feeds': {
    category: 'Team Collaboration',
    icon: FiUsers,
    content: `
## Activity Feeds

Stay updated on everything happening in your workspace.

### What's in the Activity Feed?
- Task created/updated/completed
- Comments added
- File attachments
- Status changes
- Team member actions

### Viewing Activity
- **Project Activity**: All changes in a project
- **Task Activity**: History of a specific task
- **My Activity**: Your personal activity log
- **Team Activity**: All team member actions

### Filtering Activity
- By type (tasks, comments, etc.)
- By person
- By date range
- By project

### Tips
- Check activity feed daily to stay informed
- Use it to catch up after time away
- Review for accountability and tracking
    `
  },
  'Real-time collaboration': {
    category: 'Team Collaboration',
    icon: FiUsers,
    content: `
## Real-time Collaboration

Work together seamlessly with live updates.

### Real-time Features
- See who's viewing a task
- Live updates when others make changes
- Typing indicators in comments
- Instant notifications

### Presence Indicators
- Green dot: Online now
- Yellow dot: Away/Idle
- Gray: Offline

### Collaboration Tips
- Communicate changes before making them
- Use comments for async discussion
- Set up notification preferences
- Respect others' focus time

### Conflict Resolution
When two people edit the same task:
- Latest save wins
- History preserved
- Option to review changes
    `
  },

  // Settings & Preferences
  'Account settings': {
    category: 'Settings & Preferences',
    icon: FiSettings,
    content: `
## Account Settings

Manage your personal account and profile.

### Profile Settings
- Update your name
- Change profile picture
- Set your timezone
- Add a bio

### Account Security
- Change password
- Enable two-factor authentication
- Manage connected apps
- View login history

### Email Settings
- Update email address
- Verify email
- Email preferences

### Deleting Account
- Export your data first
- Request account deletion
- 30-day grace period

### Tips
- Keep your email up to date
- Use a strong, unique password
- Enable 2FA for security
    `
  },
  'Notification preferences': {
    category: 'Settings & Preferences',
    icon: FiSettings,
    content: `
## Notification Preferences

Control how and when you receive notifications.

### Notification Channels
- **In-app**: Notifications in Tasklina
- **Email**: Updates to your inbox
- **Browser**: Desktop push notifications
- **Mobile**: Push notifications on mobile

### Customizing by Type
For each notification type, choose:
- Receive or don't receive
- Channel preferences
- Frequency (instant, digest, etc.)

### Quiet Hours
- Set do-not-disturb periods
- Choose days and times
- Emergency override option

### Project-specific
- Override global settings per project
- Mute specific projects
- Set priority for important projects
    `
  },
  'Theme customization': {
    category: 'Settings & Preferences',
    icon: FiSettings,
    content: `
## Theme Customization

Make Tasklina look the way you want.

### Theme Options
- **Light Mode**: Classic light theme
- **Dark Mode**: Easy on the eyes
- **System**: Follows your device settings

### Switching Themes
1. Go to Settings > Appearance
2. Select your preferred theme
3. Changes apply instantly

### Color Preferences
- Accent color selection
- Custom project colors
- Label color options

### Accessibility
- High contrast mode
- Reduced motion
- Font size adjustments

### Tips
- Try dark mode for night work
- Use system mode for automatic switching
- Customize colors for quick visual recognition
    `
  },
  'Keyboard shortcuts': {
    category: 'Settings & Preferences',
    icon: FiSettings,
    content: `
## Keyboard Shortcuts

Work faster with keyboard shortcuts.

### Global Shortcuts
- **N**: New task
- **P**: Go to projects
- **D**: Go to dashboard
- **/**: Search
- **?**: Show all shortcuts

### Task Shortcuts
- **Enter**: Open task details
- **E**: Edit task
- **C**: Add comment
- **A**: Assign task
- **L**: Add label

### Navigation
- **↑/↓**: Move between tasks
- **←/→**: Navigate columns (Kanban)
- **Tab**: Next field
- **Esc**: Close dialogs

### View Shortcuts
- **1**: Kanban view
- **2**: List view
- **3**: Calendar view

### Tips
- Learn a few shortcuts at a time
- Most common: N (new), / (search)
- Press ? anytime to see all shortcuts
    `
  },

  // Security & Privacy
  'Two-factor authentication': {
    category: 'Security & Privacy',
    icon: FiShield,
    content: `
## Two-Factor Authentication

Add an extra layer of security to your account.

### What is 2FA?
Two-factor authentication requires:
1. Your password (something you know)
2. A code from your device (something you have)

### Enabling 2FA
1. Go to Settings > Security
2. Click "Enable 2FA"
3. Scan QR code with authenticator app
4. Enter verification code
5. Save backup codes

### Supported Apps
- Google Authenticator
- Authy
- 1Password
- Microsoft Authenticator

### Backup Codes
- Save these securely
- Use if you lose your device
- Generate new codes after using

### Tips
- Always enable 2FA
- Keep backup codes safe
- Use a password manager
    `
  },
  'Password security': {
    category: 'Security & Privacy',
    icon: FiShield,
    content: `
## Password Security

Keep your account safe with a strong password.

### Password Requirements
- Minimum 8 characters
- Mix of letters, numbers, symbols
- No common words or patterns
- Different from other passwords

### Changing Your Password
1. Go to Settings > Security
2. Click "Change Password"
3. Enter current password
4. Enter new password (twice)
5. Click Save

### Password Tips
- Use a password manager
- Never share your password
- Don't use personal information
- Change if you suspect compromise

### Forgot Password?
1. Click "Forgot Password" on login
2. Enter your email
3. Check email for reset link
4. Create new password
    `
  },
  'Data export': {
    category: 'Security & Privacy',
    icon: FiShield,
    content: `
## Data Export

Download your data anytime.

### What Can You Export?
- Projects and tasks
- Comments and activity
- User information
- File attachments
- Settings and preferences

### Export Formats
- **JSON**: For importing to other apps
- **CSV**: For spreadsheets

### How to Export
1. Go to Settings > Data
2. Click "Export Data"
3. Select what to export
4. Choose format
5. Download when ready

### Use Cases
- Backup your data
- Move to another platform
- Create reports
- Archive old projects

### Tips
- Export regularly for backup
- Large exports may take time
- Check export for completeness
    `
  },
  'Privacy controls': {
    category: 'Security & Privacy',
    icon: FiShield,
    content: `
## Privacy Controls

Control who sees your information.

### Profile Visibility
- Public: Anyone can see
- Team: Only team members
- Private: Only you

### Activity Privacy
- Hide from activity feeds
- Control who sees your status
- Manage presence indicators

### Data Sharing
- Control data used for improvement
- Opt out of analytics
- Manage third-party integrations

### Project Privacy
- Private projects: Only invited members
- Public projects: Visible to team
- Organization projects: All org members

### Best Practices
- Review privacy settings regularly
- Use private projects for sensitive work
- Limit data sharing to necessary minimum
    `
  }
};

/**
 * HelpArticleModal component
 */
const HelpArticleModal = ({ article, articleTitle, onClose }) => {
  if (!article) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">{article.category}</p>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{articleTitle}</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="prose prose-blue max-w-none">
              {/* Simple markdown-like rendering */}
              {article.content.split('\n').map((line, index) => {
                if (line.startsWith('## ')) {
                  return <h2 key={index} className="text-2xl font-bold text-gray-900 dark:text-white mt-6 mb-4">{line.replace('## ', '')}</h2>;
                }
                if (line.startsWith('### ')) {
                  return <h3 key={index} className="text-lg font-semibold text-gray-900 dark:text-white mt-4 mb-2">{line.replace('### ', '')}</h3>;
                }
                if (line.startsWith('#### ')) {
                  return <h4 key={index} className="text-base font-semibold text-gray-800 dark:text-gray-200 mt-3 mb-1">{line.replace('#### ', '')}</h4>;
                }
                if (line.startsWith('- **')) {
                  const parts = line.replace('- **', '').split('**');
                  return (
                    <li key={index} className="ml-4 my-1">
                      <strong className="text-gray-900 dark:text-white">{parts[0]}</strong>
                      {parts[1]}
                    </li>
                  );
                }
                if (line.startsWith('- ')) {
                  return <li key={index} className="ml-4 my-1 text-gray-600 dark:text-gray-300">{line.replace('- ', '')}</li>;
                }
                if (line.match(/^\d+\./)) {
                  return <li key={index} className="ml-4 my-1 text-gray-600 dark:text-gray-300 list-decimal">{line.replace(/^\d+\.\s*/, '')}</li>;
                }
                if (line.trim() === '') {
                  return <br key={index} />;
                }
                return <p key={index} className="text-gray-600 dark:text-gray-300 my-2">{line}</p>;
              })}
            </div>
          </div>
          
          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">Was this article helpful?</p>
            <div className="flex items-center space-x-3">
              <button 
                onClick={onClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Close
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1">
                <span>Contact Support</span>
                <FiArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpArticleModal;
